// The billing-line table — ONE component, rendered by both /treatments and the
// per-patient การรักษา ledger.
//
// Extracted from src/pages/Treatments.jsx 2026-08-12. The column system is the
// contract: รายการ · จำนวน · รวม · เบิก · รวมเบิก · คลินิก · หมอ, `table-fixed`
// ("รวม" = priceXunit, what the patient pays; "รวมเบิก" = priceActualTotal,
//  that plus anything claimed from ปกส./ประกัน. Renamed from เก็บเอง/รวม 2026-08-13.)
// with a shared <colgroup> so a group header's totals line up exactly above the
// lines beneath it. Both pages import THIS rather than restating the markup —
// "identical column order and meaning" is otherwise a promise no code enforces.
//
// รวม (priceXunit) is the emphasised column everywhere (teal, bold, tinted
// cell): it is the money reconciled against the cash drawer at close.

import { ChevronDown, ArrowUpDown, Gift, AlertTriangle, Wallet } from 'lucide-react'
import {
  bXunit, bAdvance, bAdvancePayer, bMissingClaimRef, bClinic, bDoctor, bTotal, bUnit, bPrice,
  bName, bTypeTag, bPromoName, bHasPromo, bFixedDF, bMismatch, bDetailNote,
  bPctDoctor, bZeroDF, bClaimStatus, BILLING_SORTS, TEAL,
  bClaimReceived, OVERHEAD_TYPE_IDS, isOverheadLine,
} from '../../lib/billing.js'
import { Badge, EmptyState, fmtMoney, fmtDate } from '../../kit'

// Whole baht, Math.round FIRST, then the kit formats it — the rounding fmtBill
// always used (fmtMoney alone rounds a negative half away from zero), so these
// lines and the card / group totals above them never disagree by a baht.
// Display only; nothing here is sent anywhere.
const fmt = (n) => fmtMoney(n == null || n === '' ? n : Math.round(Number(n)))

// รายการ · รวม · เบิก · รวมเบิก · คลินิก · หมอ · %หมอ
// จำนวน was dropped 2026-08-14 — it is 1 on almost every line, so a whole column
// carried almost no information; N > 1 now shows as ×N after the item name.
// The edit/delete action column went with it: the ROW opens the editor, and
// delete lives inside that editor.
export const BILL_WIDTHS = [null, 92, 96, 92, 78, 88, 56]

export function BillColgroup() {
  return <colgroup>{BILL_WIDTHS.map((w, i) => <col key={i} style={w ? { width: w } : undefined} />)}</colgroup>
}

// The 5 money cells (รวม·เบิก·รวมเบิก·คลินิก·หมอ) matching BillColgroup cols 2–6,
// plus the empty %หมอ cell so a group header still spans the full width.
export function BillTotalCells({ patient, advance, total, clinic, doctor }) {
  return (
    <>
      <td className="text-right px-2 tabular-nums font-bold text-[13px] bg-teal-50/60 whitespace-nowrap align-middle" style={{ color: TEAL }}>{fmt(patient)}</td>
      <td className="text-right px-2 tabular-nums text-indigo-500 whitespace-nowrap align-middle">{advance ? fmt(advance) : <span className="text-slate-300">—</span>}</td>
      <td className="text-right px-2 tabular-nums font-bold text-slate-900 whitespace-nowrap align-middle">{fmt(total)}</td>
      <td className="text-right px-2 tabular-nums text-slate-500 whitespace-nowrap align-middle">{fmt(clinic)}</td>
      <td className="text-right px-2 tabular-nums text-slate-500 whitespace-nowrap align-middle">{fmt(doctor)}</td>
      <td className="pr-4" />
    </>
  )
}

export function SortTh({ label, sortKey, sort, onSort, align = 'right', emphasize }) {
  const active = sort === sortKey
  const base = align === 'left' ? 'text-left' : 'text-right'
  return (
    <th className={`${base} font-medium py-1.5 px-2 ${emphasize ? 'bg-teal-50/70' : ''}`}>
      <button onClick={() => onSort?.(sortKey)}
        className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''} ${active ? 'font-bold' : 'hover:text-slate-600'}`}
        style={emphasize ? { color: TEAL } : (active ? { color: '#334155' } : undefined)}>
        {label}
        {active ? <ChevronDown size={12} /> : <ArrowUpDown size={11} className="opacity-40" />}
      </button>
    </th>
  )
}

export function PaymentBadge({ name, color, none }) {
  const c = none ? '#dc2626' : (color || '#059669')
  return <Badge color={c}><Wallet size={11} strokeWidth={2} />{name}</Badge>
}

/**
 * `showClaim` adds the claim-status chip (ค้างรับ / รับแล้ว) under an advance-pay
 * line. Off on /treatments — that page is a single day and reconciles cash, not
 * outstanding claims — and on for the patient ledger, where "who still owes us"
 * is the point.
 */
/**
 * PER-VISIT OVERHEAD, in the order it sinks to the bottom of every table.
 *
 * These are not treatments. They are the charges a visit carries regardless of
 * what was done — the sterilisation fee, and the two lines autofeeBilling
 * maintains by machine (the 1.5% card fee and the 1% insurance fee). Sorting
 * them in with the clinical items puts a constant ฿100 second in a five-line
 * bill and makes the reader re-find the actual treatment every time.
 *
 * So they are PINNED last under every sort, and greyed. A receipt reads the same
 * way: items, then fees, then the total. The sort still owns everything above
 * them; the block itself keeps this fixed internal order so it does not reshuffle
 * when the user flips a column.
 *
 * Not hidden and not collapsed — they are real money in the รวม, and a fee the
 * patient is paying must stay readable. Only their PRIORITY changes.
 */
// The ids and the test live in lib/billing.js (the drawer's treatment summary skips them too).
const overheadRank = (b) => OVERHEAD_TYPE_IDS.indexOf(Number(b?.billing_type_id))

export function BillingTable({ lines, showFooter = true, sort = 'patient', onSort, showClaim = false, onRowClick = null }) {
  if (!lines.length) {
    return <div className="px-4 py-1"><EmptyState variant="compact" title="ไม่มีรายการบิล" /></div>
  }
  let patient = 0, advance = 0, clinic = 0, doctor = 0, total = 0
  for (const b of lines) { patient += bXunit(b); advance += bAdvance(b); clinic += bClinic(b); doctor += bDoctor(b); total += bTotal(b) }
  const cmp = (BILLING_SORTS[sort] || BILLING_SORTS.patient).fn
  // Two lists, not one comparator: a comparator that also carried the pin would
  // have to be threaded through BILLING_SORTS and every future sort key would
  // have to remember it. Partitioning here makes the rule unmissable and applies
  // it to sorts that do not exist yet.
  const items = lines.filter((b) => !isOverheadLine(b)).sort(cmp)
  const overhead = lines.filter(isOverheadLine).sort((a, b) => overheadRank(a) - overheadRank(b))
  const sorted = [...items, ...overhead]
  const firstOverhead = items.length
  return (
    <table className="w-full text-[12.5px] table-fixed">
      <BillColgroup />
      <thead>
        <tr className="text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50/70">
          <SortTh label="รายการ" sortKey="type" sort={sort} onSort={onSort} align="left" />
          <SortTh label="รวม" sortKey="patient" sort={sort} onSort={onSort} emphasize />
          <th className="text-right font-medium py-1.5 px-2">เบิก</th>
          <SortTh label="รวมเบิก" sortKey="total" sort={sort} onSort={onSort} />
          <th className="text-right font-medium py-1.5 px-2">คลินิก</th>
          <th className="text-right font-medium py-1.5 px-2">หมอ</th>
          <th className="text-right font-medium py-1.5 pr-4">%หมอ</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((b, i) => (
          <BillingLine key={b.id ?? i} b={b} showClaim={showClaim} onRowClick={onRowClick}
            muted={i >= firstOverhead} startsOverhead={i === firstOverhead && i > 0} />
        ))}
      </tbody>
      {showFooter && (
        <tfoot>
          <tr className="border-t border-slate-100 bg-slate-50/50 font-semibold text-slate-800">
            <td className="py-2 pl-4">รวม</td>
            <td className="text-right px-2 tabular-nums font-bold bg-teal-50/60" style={{ color: TEAL }}>{fmt(patient)}</td>
            <td className="text-right px-2 tabular-nums text-indigo-600">{fmt(advance)}</td>
            <td className="text-right px-2 tabular-nums">{fmt(total)}</td>
            <td className="text-right px-2 tabular-nums">{fmt(clinic)}</td>
            <td className="text-right px-2 tabular-nums">{fmt(doctor)}</td>
            <td className="pr-4" />
          </tr>
        </tfoot>
      )}
    </table>
  )
}

export function BillingLine({ b, showClaim = false, onRowClick = null, muted = false, startsOverhead = false }) {
  const mismatch = bMismatch(b)
  const note = bDetailNote(b)
  const pct = bPctDoctor(b)
  const advPayer = bAdvancePayer(b)
  const nameSameAsNote = note && note === bName(b)
  // Normalised in lib/billing.js so the chip, the ค้างรับ headline and the
  // "เฉพาะที่ค้างรับ" filter can never disagree about the same field.
  const claim = bClaimStatus(b)
  const claimable = showClaim && bAdvance(b) !== 0 && claim !== ''
  const unit = bUnit(b)
  return (
    <tr
      onClick={onRowClick ? () => onRowClick(b) : undefined}
      title={onRowClick ? 'แก้ไขรายการนี้' : undefined}
      className={`border-t ${startsOverhead ? 'border-slate-200' : 'border-slate-50'} ${
        mismatch ? 'bg-amber-50/40' : muted ? 'bg-slate-50/50' : ''
      } ${onRowClick ? 'cursor-pointer hover:bg-brand-50/40 transition-colors' : ''}`}>
      <td className="py-2 pl-4 align-top">
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Name first, category chip after it: `billing_type.name` is the line
              you actually read ("ขูดหินปูน"), `type` is the bucket it falls in
              ("GP"). Leading with the bucket made every row start with a grey
              tag and pushed the differing part to a ragged second column. */}
          <span className={muted ? 'text-slate-500' : 'text-slate-700 font-medium'}>{bName(b)}</span>
          {/* จำนวน has no column any more — it is 1 on almost every line, so it
              only earns space on the ones where it isn't. */}
          {unit > 1 && (
            <span className="text-[11px] font-bold text-slate-500 tabular-nums" title={bPrice(b) !== 0 ? `ราคา/หน่วย ${fmt(bPrice(b))}` : undefined}>×{unit}</span>
          )}
          {bTypeTag(b) && <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 px-1 py-0.5 rounded shrink-0">{bTypeTag(b)}</span>}
          {bHasPromo(b) && <span className="inline-flex items-center gap-0.5 rounded bg-pink-50 text-pink-600 px-1.5 py-0.5 text-[10px] font-medium"><Gift size={9} />{bPromoName(b) || 'โปรฯ'}</span>}
          {bFixedDF(b) && <span className="rounded bg-slate-100 text-slate-500 px-1.5 py-0.5 text-[10px] font-medium">Fixed DF</span>}
          {/* A 0 DF removes this line's PATIENT REVENUE from the doctor's month
              too (gen3 filters priceDoctor != 0), so it has to be visible. */}
          {showClaim && bZeroDF(b) && <span className="rounded bg-slate-100 text-slate-500 px-1.5 py-0.5 text-[10px] font-medium" title="ไม่จ่าย DF — รายการนี้จะไม่เข้ายอดของหมอในเดือนนั้น">ไม่จ่าย DF</span>}
          {mismatch && <AlertTriangle size={12} className="text-amber-500" title="คลินิกจริง + หมอ ไม่เท่ายอดจริง" />}
          {bMissingClaimRef(b) && (
            <span className="inline-flex items-center gap-0.5 rounded bg-rose-50 text-rose-600 ring-1 ring-inset ring-rose-200 px-1.5 py-0.5 text-[10px] font-semibold"
              title={`เบิก ${advPayer} แต่ยังไม่ได้กรอกรหัสเคลมประกัน`}>
              <AlertTriangle size={9} />ไม่มีรหัสเคลม
            </span>
          )}
        </div>
        {note && !nameSameAsNote && <div className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">{note}</div>}
        {claimable && <ClaimChip status={claim} at={b.advance_received_at} payer={advPayer} amount={bClaimReceived(b)} />}
      </td>
      <td className="text-right px-2 tabular-nums font-bold text-[13px] bg-teal-50/60 align-top whitespace-nowrap" style={{ color: TEAL }}>
        {fmt(bXunit(b))}
      </td>
      <td className="text-right px-2 tabular-nums align-top whitespace-nowrap">
        {bAdvance(b) !== 0
          ? <span className="text-indigo-600">{fmt(bAdvance(b))}{advPayer && <span className="text-indigo-400 text-[10.5px]"> {advPayer}</span>}</span>
          : <span className="text-slate-300">—</span>}
      </td>
      <td className={`text-right px-2 tabular-nums font-semibold align-top whitespace-nowrap ${mismatch ? 'text-amber-600' : 'text-slate-900'}`}>{fmt(bTotal(b))}</td>
      <td className="text-right px-2 tabular-nums text-slate-500 align-top">{fmt(bClinic(b))}</td>
      <td className="text-right px-2 tabular-nums text-slate-500 align-top whitespace-nowrap">{fmt(bDoctor(b))}</td>
      <td className="text-right pr-4 tabular-nums text-slate-400 text-[11px] align-top whitespace-nowrap">{pct != null ? `${pct}%` : ''}</td>
    </tr>
  )
}

// Wording matches /reports/reimbursement so the two pages agree: this is money a
// PAYER (ปกส./ประกัน) owes the clinic — "ค้างรับ" — never money the patient owes.
//
// ⚠️ FOUR STATES, NOT A BOOLEAN. This was `received ? … : 'ค้างรับ'`, which is actively
// false for a written-off line: it would have shown an amber "ยังไม่ได้รับเงิน" chip on
// money the clinic has formally given up on. A status this drives must be enumerated,
// never inverted from one case.
const CLAIM_CHIP = {
  received: {
    label: 'รับแล้ว',
    cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    title: (p, w) => `รับเงินจาก${p}แล้ว${w ? ` ${w}` : ''}`,
  },
  pending: {
    label: 'ค้างรับ',
    cls: 'bg-amber-50 text-amber-700 ring-amber-200',
    title: (p) => `ยังไม่ได้รับเงินจาก${p}`,
  },
  partial: {
    label: 'รับบางส่วน',
    cls: 'bg-sky-50 text-sky-700 ring-sky-200',
    title: (p) => `${p}จ่ายน้อยกว่าที่เรียกเก็บ — ปิดรายการที่ยอดที่ได้รับจริง`,
  },
  rejected: {
    label: 'เบิกไม่ได้',
    cls: 'bg-slate-100 text-slate-500 ring-slate-300 line-through',
    title: (p) => `ตัดเป็นเบิกไม่ได้ — ยอมรับแล้วว่าจะไม่ได้รับเงินจาก${p}`,
  },
}

function ClaimChip({ status, at, payer, amount }) {
  const spec = CLAIM_CHIP[status]
  if (!spec) return null
  const who = payer || 'ผู้จ่าย'
  // Screen date = kit dd/mm/yyyy ค.ศ., read in Bangkok (decision #1).
  const when = at ? fmtDate(at) : ''
  const shown = when === '—' ? '' : when
  const suffix = status === 'received' && shown ? ` · ${shown}`
    : status === 'partial' && amount ? ` · ${fmtMoney(amount, { maxDecimals: 2 })}`
      : ''
  return (
    <span
      title={spec.title(who, shown)}
      className={`mt-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10.5px] font-medium ring-1 ${spec.cls}`}
    >
      {spec.label}{suffix}
    </span>
  )
}
